import { Link } from "react-router-dom";
import { Kicker } from "@/components/ui/kicker";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { StepScreenshot } from "@/components/walkthrough/step-screenshot";
import type { Severity } from "@/components/finding-badges";
import type { FindingDetailEvidence } from "./finding-detail-types";

interface FindingEvidenceSectionProps {
  projectId: string;
  evidence: FindingDetailEvidence;
  title: string;
  severity: Severity;
  type?: string;
  personaName?: string | null;
  personaRole?: string | null;
}

export function FindingEvidenceSection({
  projectId,
  evidence,
  title,
  severity,
  type,
  personaName,
  personaRole,
}: FindingEvidenceSectionProps) {
  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <Kicker as="h2">Where it happened</Kicker>
        <Link
          to={`/projects/${projectId}/walkthroughs/${evidence.walkthroughId}?tab=trace&step=${evidence.stepNumber}`}
          className="text-xs text-muted-foreground underline decoration-border underline-offset-4 hover:text-foreground"
        >
          View in walkthrough run →
        </Link>
      </div>

      {/* Step Context & Persona Voice */}
      {(evidence.stepTitle ||
        evidence.personaReasoning ||
        evidence.surface ||
        evidence.pageUrl) && (
        <div className="rounded-lg border border-border bg-surface-tonal/60 p-4 space-y-3 max-w-3xl">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground border-b border-border/50 pb-2">
            <span className="font-medium text-foreground">
              Step {evidence.stepNumber}
              {evidence.stepTitle ? `: ${evidence.stepTitle}` : ""}
            </span>
            {evidence.pageUrl && (
              <span
                className="font-mono truncate max-w-[42ch]"
                title={evidence.pageUrl}
              >
                {evidence.pageUrl}
              </span>
            )}
          </div>

          {evidence.personaReasoning && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {personaName || "Persona"}
                </span>
                {personaRole && <span>· {personaRole}</span>}
              </div>
              <PersonaVoice size="sm">
                “
                {evidence.personaReasoning
                  .replace(/^[A-Za-zÀ-ÖØ-öø-ÿ]+\s*:\s*/, "")
                  .replace(/^["“”]+|["“”]+$/g, "")}
                ”
              </PersonaVoice>
            </div>
          )}
        </div>
      )}

      <figure className="space-y-2">
        <div className="max-w-3xl overflow-hidden rounded-md border border-border bg-surface-tonal">
          <StepScreenshot
            walkthroughId={evidence.walkthroughId}
            stepNumber={evidence.stepNumber}
            targetBox={evidence.targetBox}
            targetTone={severity || type}
            highlights={
              evidence.targetBox
                ? [
                    {
                      id: "finding-target",
                      box: evidence.targetBox,
                      tone: severity || type,
                      label: title ?? (type === "helps" ? "Strength" : "Issue"),
                      isActive: true,
                    },
                  ]
                : undefined
            }
            alt={`Step ${evidence.stepNumber} of the walkthrough, where this was seen`}
          />
        </div>
        <figcaption className="text-xs text-muted-foreground">
          Step {evidence.stepNumber} of the walkthrough
          {evidence.surface ? ` · ${evidence.surface}` : ""}
        </figcaption>
      </figure>
    </section>
  );
}
